// src/pages/MarketPulse.jsx
import React, { useState, useMemo } from "react";
import dayjs from "dayjs";
import {
    Box,
    Tabs,
    Tab,
    TextField,
    Select,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    OutlinedInput,
    InputLabel,
    Typography,
    useTheme,
    CircularProgress,
    FormControl,
    Checkbox,
    ListItemText,
    Accordion,
    AccordionSummary,
    AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { DataGrid } from "@mui/x-data-grid";
import {
    DatePicker,
    LocalizationProvider,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { tokens } from "../../../theme"
import { skipToken } from "@reduxjs/toolkit/query/react";

import { DateRangePicker, LocalizationProvider as LocalizationProProvider } from "@mui/x-date-pickers-pro";


import {
    useGetDailyCountsQuery,
    useGetTopicBreakoutsQuery,
    useGetTopicDailyTrendQuery
} from "../../../features/market_pulse/marketPulseApiSlice";

import DailyTab from "./tabs/DailyTab";
import TopicTab from "./tabs/TopicTab";



/* ---------- reusable helpers -------------------------------------------- */
const TabPanel = ({ index, value, children }) =>
    value === index && <Box sx={{ p: 2 }}>{children}</Box>;

const makeSearchFilter = (fields, term) =>
    term
        ? (row) =>
            fields.some((f) =>
                String(row[f] ?? "").toLowerCase().includes(term.toLowerCase())
            )
        : () => true;

/* ---------- main component ---------------------------------------------- */
const MarketPulse = () => {
    const [tab, setTab] = useState(0);

    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    /* DAILY COUNTS TAB ------------------------------------------------------ */
    const [selectedDate, setSelectedDate] = useState(dayjs());

    /* TOPIC BREAKOUT TABS --------------------------------------------------- */
    const [timeframe, setTimeframe] = useState("day"); // day | week | month
    const [search, setSearch] = useState("");

    /* ---- search-field selector ----------------------------------------- */
    const SEARCH_OPTS = [
        { label: "Topic ID", value: "TOPIC_ID" },
        { label: "Topic", value: "TOPIC" },
        { label: "Description", value: "DESCRIPTION" },
    ];
    const [searchFields, setSearchFields] = useState(
        SEARCH_OPTS.map((o) => o.value),        // all selected by default
    );

    /* ------------------------------------------------------------------- *
     * 1.  Fetch the three API endpoints                                   *
     * ------------------------------------------------------------------- */
    const { data: dailyRows = [], isFetching: dailyFetching } =
        useGetDailyCountsQuery(selectedDate.format("YYYY-MM-DD"));

    // B2B and B2C breakouts (re-run when date or timeframe changes)
    const { data: b2bResp = { rows: [] },
        isLoading: b2bLoading,
        isFetching: b2bFetching } = useGetTopicBreakoutsQuery(
            {
                type: "b2b",
                range: timeframe,
                date: selectedDate.format("YYYY-MM-DD"),

            },
            { refetchOnMountOrArgChange: true }        // ensure refetch on every arg change
        );

    const { data: b2cResp = { rows: [] },
        isLoading: b2cLoading,
        isFetching: b2cFetching } = useGetTopicBreakoutsQuery(
            {
                type: "b2c",
                range: timeframe,
                date: selectedDate.format("YYYY-MM-DD"),
            },
            { refetchOnMountOrArgChange: true }
        );

    const b2bRows = b2bResp.rows;
    const b2cRows = b2cResp.rows;



    /* ------------------------------------------------------------------- */
    /* filtering                                                           */
    /* ------------------------------------------------------------------- */
    const filterFn = useMemo(
        () => makeSearchFilter(searchFields, search),
        [search, searchFields]
    );
 




    /* timeline *start* — end is always +6 days ------------------------------ */
    const [startDay, setStartDay] = useState(dayjs().subtract(6, "day"));
    const timelineRange = [startDay, startDay.clone().add(6, "day")];

    /* build the 7-day column set on the fly */
    const dayColDefs = useMemo(() => {
        const [s, e] = timelineRange;
        const days = [];
        for (let d = s.clone(); d.isBefore(e) || d.isSame(e); d = d.add(1, "day"))
            days.push(d.clone());

        return [
            { field: "TOPIC_ID", headerName: "Topic ID", minWidth: 120 },
            { field: "TOPIC", headerName: "Topic", minWidth: 160, flex: 1 },
            ...days.map((d) => ({
                field: d.format("YYYY-MM-DD"),
                headerName: d.format("MMM D"),
                type: "number",
                minWidth: 110,
                valueFormatter: ({ value }) =>
                    value !== undefined ? value.toLocaleString() : "",
            })),
        ];
    }, [timelineRange]);

    /* quick helper to reshape the breakout rows into a topic-by-day map */
    const buildTimelineRows = (rows) => {
        const map = {};
        rows.forEach((r) => {
            const id = r.TOPIC_ID;
            if (!map[id])
                map[id] = {
                    TOPIC_ID: id,
                    TOPIC: r.TOPIC,
                    DESCRIPTION: r.DESCRIPTION,
                };
            map[id][selectedDate.format("YYYY-MM-DD")] = r.RECORD_COUNT; // DAY agg row already
        });
        return Object.values(map);
    };

    const handleDateChange = (newValue) => {
        if (newValue) {
            // strip time for consistency
            setSelectedDate(newValue.startOf("day"));
        }
    };

    const tabStyles = {
        // H-5 size & weight
        fontSize: theme.typography.h5.fontSize,
        fontWeight: theme.typography.h5.fontWeight,

        color: colors.primary[100],            // default
        "&.Mui-selected": {
            color: colors.greenAccent[400],      // selected
        },
        "&:hover": {
            color: colors.primary[300],          // hover
        },
    };

    /* JSX ------------------------------------------------------------------- */
    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box>
                <Typography variant="h4" sx={{ mb: 2 }}>
                    Market Pulse
                </Typography>

                {/* tab header */}
                <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
                    <Tab label="Daily Counts" sx={tabStyles} />
                    <Tab label="B2B Topics" sx={tabStyles} />
                    <Tab label="B2C Topics" sx={tabStyles} />
                </Tabs>

                {/* ----------------------------------------------------------------- */}
                {/* 1 — Daily Counts tab                                             */}
                {/* ----------------------------------------------------------------- */}
                <TabPanel value={tab} index={0}>



                    <DailyTab
                        rows={dailyRows}
                        loading={dailyFetching}
                        date={selectedDate}
                        onDateChange={handleDateChange}
                    />
                </TabPanel>

                {/* ----------------------------------------------------------------- */}
                {/* 2 & 3 — Topic breakout tabs                                      */}
                {/* ----------------------------------------------------------------- */}
                <TabPanel value={tab} index={1}>
                    <TopicTab
                        type="b2b"
                        timeframe={timeframe}
                        selectedDate={selectedDate}
                        search={search}
                        searchFields={searchFields}
                        setSearch={setSearch}
                        setSearchFields={setSearchFields}
                        startDay={startDay}
                        setStartDay={setStartDay}
                    />

                </TabPanel>

                <TabPanel value={tab} index={2}>
                    <TopicTab
                        type="b2c"
                        timeframe={timeframe}
                        selectedDate={selectedDate}
                        search={search}
                        searchFields={searchFields}
                        setSearch={setSearch}
                        setSearchFields={setSearchFields}
                        startDay={startDay}
                        setStartDay={setStartDay}
                    />
                </TabPanel>
            </Box>
        </LocalizationProvider>
    );
};

export default MarketPulse;
